import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@amzn/awsui-components-react';

import { useEventContext } from '../../contexts';
import FEATURES from '../../constants/features';
import starImage from '../../images/star_gold_filled.svg';

import './FeedbackStarsAnimation.css';

const numStars = 5;
const animationStepDurationDefault = 325;
const animationStepDelayDefault = 325;
const twentyMinutesInMs = 1000 * 60 * 20;
const labIDRegex = '/lab/.+/[a-zA-Z]{2}-[a-zA-Z]{2}/.+';

const FeedbackStarsAnimation = ({
  children,
  animationStartDelay = twentyMinutesInMs,
  animationStepDelay = animationStepDelayDefault,
  animationStepDuration = animationStepDurationDefault,
  postAnimationDelay = 1000,
}) => {
  const { event } = useEventContext();
  const animationEnabled = event.hasFeature(FEATURES.feedbackAnimation);
  const [showStars, setShowStars] = useState(false);
  const [animationIndex, setAnimationIndex] = useState(0);
  const inFocus = useRef(true);
  const animateOnNextFocus = useRef(false);
  const animationStepTimeout = useRef(null);
  const animationCountdownTimeout = useRef(null);
  const [hasHovered, setHasHovered] = useState(false);
  const history = useHistory();

  const focusListener = useCallback(() => {
    inFocus.current = true;
    if (animateOnNextFocus.current) {
      setShowStars(true);
      animateOnNextFocus.current = false;
    }
  }, []);
  const blurListener = useCallback(() => {
    inFocus.current = false;
  }, []);
  const startAnimationCountdownOnLabStartFn = useCallback(
    path => {
      if (path.match(labIDRegex)) {
        clearTimeout(animationCountdownTimeout.current);
        animationCountdownTimeout.current = setTimeout(() => {
          if (inFocus.current) {
            setShowStars(true);
          } else {
            animateOnNextFocus.current = true;
          }
        }, animationStartDelay);
      }
    },
    [animationStartDelay]
  );

  useEffect(() => {
    if (!animationEnabled) {
      return;
    }
    window.addEventListener('focus', focusListener);
    window.addEventListener('blur', blurListener);
    startAnimationCountdownOnLabStartFn(window.location.pathname);
    const historyTeardownFn = history.listen(location => {
      startAnimationCountdownOnLabStartFn(location.pathname);
    });
    return () => {
      historyTeardownFn();
      clearTimeout(animationCountdownTimeout.current);
      window.removeEventListener('focus', focusListener);
      window.removeEventListener('blur', blurListener);
    };
  }, [
    startAnimationCountdownOnLabStartFn,
    focusListener,
    blurListener,
    animationEnabled,
    history,
  ]);

  useEffect(() => {
    if (showStars) {
      animationStepTimeout.current = setTimeout(() => {
        if (animationIndex >= numStars - 1) {
          clearTimeout(animationStepTimeout.current);
          setTimeout(() => {
            setShowStars(false);
            setAnimationIndex(0);
            if (!hasHovered) {
              setTimeout(() => {
                setShowStars(true);
              }, postAnimationDelay);
            }
          }, postAnimationDelay);
        } else {
          setAnimationIndex(animationIndex + 1);
        }
      }, animationStepDelay);
    } else {
      clearTimeout(animationStepTimeout.current);
    }
    return () => {
      clearTimeout(animationStepTimeout.current);
    };
  }, [
    animationIndex,
    showStars,
    animationStepDelay,
    postAnimationDelay,
    hasHovered,
  ]);

  const stars = [];
  if (showStars) {
    for (let i = 0; i < numStars; i++) {
      stars.push(
        <img
          data-testid={`feedback-star-${i}`}
          key={`feedback-star-${i}`}
          style={{
            height: '15px',
            marginRight: '.25rem',
            transition: `opacity ${animationStepDuration}ms`,
            opacity: animationIndex >= i ? '1' : '0',
          }}
          src={starImage}
          alt=""
        ></img>
      );
    }
  }
  return (
    <span
      className="FeedbackStarsAnimation__content"
      onMouseEnter={() => {
        if (!hasHovered) {
          setHasHovered(true);
        }
      }}
    >
      <>
        {showStars && stars}
        <Box
          color="inherit"
          variant="span"
          fontWeight="bold"
          className={showStars ? 'awsui-util-hide' : ''}
        >
          {children}
        </Box>
      </>
    </span>
  );
};

export default FeedbackStarsAnimation;
